import { z } from "zod";
/**
 * Non-negative integer. Deliberately wider than the sendable range: a request
 * that never completed reports `0`, and that is worth surfacing to the caller
 * rather than erasing. Use `isSendableHttpStatus` before putting one on the
 * wire.
 */
const StatusSchema = z.int().min(0).optional().catch(undefined);
/**
 * The fields we are willing to recognise on a caught value.
 *
 * Each field catches independently so that one field of the wrong type does not
 * discard the rest: a `{ statusCode: "403", message: "no" }` still yields the
 * message.
 */
const ThrownErrorSchema = z.object({
    statusCode: StatusSchema,
    status: StatusSchema,
    message: z.string().optional().catch(undefined),
    stack: z.string().optional().catch(undefined),
    code: z.string().optional().catch(undefined),
    name: z.string().optional().catch(undefined),
});
/**
 * Narrow a caught value to the fields we recognise. Never throws, whatever was
 * thrown and however the schema above is later changed — callers run this as
 * the first statement of a `catch`, where a second exception would bury the
 * first.
 */
export const parseThrownError = (error) => {
    try {
        return ThrownErrorSchema.parse(error);
    }
    catch {
        // `throw` permits non-objects, which fail the parse; and reading a property
        // can itself throw, via a getter or a hostile Proxy.
        return {};
    }
};
/**
 * The message of a caught value, falling back to its string form when it
 * carries none. Duck-typed rather than `instanceof Error`, so objects thrown by
 * libraries that do not subclass `Error` are still described usefully. Never
 * throws, even when the value cannot be converted to a string.
 */
export const getErrorMessage = (error) => {
    const message = parseThrownError(error).message;
    if (message !== undefined)
        return message;
    try {
        return String(error);
    }
    catch {
        return "Unknown error";
    }
};
/**
 * Whether a status can be sent as an HTTP response code: a three-digit integer
 * (RFC 9110). Notably excludes the `0` reported when a request never completed
 * — a real value worth logging, but one no server can send, and one that HTTP
 * frameworks reject outright.
 */
export const isSendableHttpStatus = (status) => status !== undefined &&
    Number.isInteger(status) &&
    status >= 100 &&
    status <= 999;
//# sourceMappingURL=thrownError.js.map